// src/components/Warehouse/warehouseTableData.js

import React from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useLanguage } from "context/LanguageContext";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Component for action buttons
const WarehouseTableActions = ({ row, handleDelete }) => {
  return (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          /* Implement edit logic here */
          // For example, you might open an edit modal with the item's details
          console.log("Edit item:", row.original.id);
        }}
      >
        <Edit />
      </IconButton>
      <IconButton color="error" size="small" onClick={() => handleDelete(row.original.id)}>
        <Delete />
      </IconButton>
    </>
  );
};

// PropTypes for the WarehouseTableActions component
WarehouseTableActions.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number.isRequired, // Ensure 'id' is a number
    }).isRequired,
  }).isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// Delete button component
const DeleteButton = ({ warehouseId, handleDelete }) => {
  const { t } = useLanguage();

  return (
    <MDButton
      variant="outlined"
      color="error"
      size="small"
      onClick={() => handleDelete(warehouseId)}
    >
      {t("delete")}
    </MDButton>
  );
};

DeleteButton.propTypes = {
  warehouseId: PropTypes.number.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// --- Reusable Sub-components defined outside for performance and clarity ---

const Author = ({ name }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDTypography display="block" variant="button" fontWeight="medium">
      {name}
    </MDTypography>
  </MDBox>
);
Author.propTypes = {
  name: PropTypes.string,
};
Author.defaultProps = {
  name: "",
};

const Job = ({ title }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
      {title}
    </MDTypography>
  </MDBox>
);
Job.propTypes = {
  title: PropTypes.string,
};
Job.defaultProps = {
  title: "",
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox>
    <Tooltip title="Redaguoti">
      <IconButton onClick={() => handleEdit(row.original)} color="info">
        <Edit />
      </IconButton>
    </Tooltip>
    <Tooltip title="Ištrinti">
      <IconButton onClick={() => handleDelete(row.original.id)} color="error">
        <Delete />
      </IconButton>
    </Tooltip>
  </MDBox>
);

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// Cell components with proper PropTypes
const ItemCodeCell = ({ cell }) => <Author name={cell.value} />;
ItemCodeCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const WarehouseCodeCell = ({ cell }) => <Author name={cell.value} />;
WarehouseCodeCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const NameCell = ({ cell }) => <Job title={cell.value} />;
NameCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const GroupCell = ({ cell }) => <Job title={cell.value} />;
GroupCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

const ActionsCellWrapper = ({ row, handleEdit, handleDelete }) => (
  <ActionsCell row={row} handleEdit={handleEdit} handleDelete={handleDelete} />
);
ActionsCellWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

// --- Main Table Definition ---

const warehouseTableData = (handleDelete, handleEdit) => {
  return {
    columns: [
      {
        Header: "Prekės kodas",
        accessor: "item_code",
        width: "25%",
        Cell: ItemCodeCell,
      },
      {
        Header: "Sandėlio kodas",
        accessor: "warehouse_code",
        width: "20%",
        Cell: WarehouseCodeCell,
      },
      {
        Header: "Pavadinimas",
        accessor: "name",
        Cell: NameCell,
      },
      {
        Header: "Grupė",
        accessor: "group",
        width: "25%",
        Cell: GroupCell,
      },
      {
        Header: "Veiksmai",
        accessor: "actions",
        width: "10%",
        Cell: (props) => (
          <ActionsCellWrapper {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
  };
};

warehouseTableData.propTypes = {
  handleDelete: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
};

export default warehouseTableData;
